/**
 * Decides whether a link rendered inside a Scout chat message points somewhere
 * inside the app (so we can navigate with react-router and keep Scout open)
 * versus an external destination (which should still open in a new tab).
 *
 * Returns the router `to` value (pathname + search + hash) for an in-app link,
 * or `undefined` when the href is external, unparseable, or a non-navigational
 * scheme (`#`, `mailto:`, `tel:`, etc.).
 */
export function resolveInternalPath(href: string | undefined): string | undefined {
  if (!href) {
    return undefined;
  }

  const trimmed = href.trim();

  // In-page anchors and non-http schemes are not app navigations.
  if (trimmed === "" || trimmed.startsWith("#")) {
    return undefined;
  }

  // Root-relative app path.
  if (trimmed.startsWith("/")) {
    // Protocol-relative URLs ("//host/path") are external.
    if (trimmed.startsWith("//")) {
      return undefined;
    }
    return trimmed;
  }

  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const url = new URL(trimmed, window.location.origin);
    if (url.origin !== window.location.origin) {
      return undefined;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}
